import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, FileCheck2, Globe2, HardHat, ShieldAlert, Church, Landmark, BrainCircuit, Fingerprint, Lock, LifeBuoy, ChevronRight } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/16520011/pexels-photo-16520011.jpeg";
const SERVICES_IMG = "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4";

const services = [
  {
    icon: ShieldCheck,
    title: "Security Plans",
    blurb: "Threat assessments, physical-security architecture, and incident-response playbooks — including SVA & SSP development under CFATS and API-780.",
    testid: "service-card-security",
  },
  {
    icon: Globe2,
    title: "Export Compliance",
    blurb: "ITAR, EAR, and OFAC programs. Classification, licensing, and screening built for audit.",
    testid: "service-card-export",
  },
  {
    icon: Landmark,
    title: "FAR / DFARS Compliance",
    blurb: "Federal contracting compliance for primes and suppliers — clause flow-down, Section 889 supply-chain screening, and mandatory-disclosure programs.",
    testid: "service-card-far",
  },
  {
    icon: Lock,
    title: "CMMC",
    blurb: "Cybersecurity Maturity Model Certification for defense contractors — NIST SP 800-171 implementation, POA&M remediation, and C3PAO assessment readiness.",
    testid: "service-card-cmmc",
  },
  {
    icon: BrainCircuit,
    title: "AI Governance & Compliance",
    blurb: "Governance programs for AI systems — model inventory, risk assessment, and compliance with the EU AI Act, NIST AI RMF, and ISO/IEC 42001.",
    testid: "service-card-ai",
  },
  {
    icon: Fingerprint,
    title: "Privacy Compliance",
    blurb: "Privacy programs built for GDPR, CCPA/CPRA, and the widening web of data-protection law — data mapping, rights handling, and breach readiness.",
    testid: "service-card-privacy",
  },
  {
    icon: HardHat,
    title: "Safety Consulting",
    blurb: "OSHA-aligned safety management systems, hazard analyses, and continuous-improvement programs.",
    testid: "service-card-safety",
  },
  {
    icon: LifeBuoy,
    title: "Business Continuity",
    blurb: "Continuity and disaster-recovery programs that keep critical operations running — business impact analysis, COOP/DR plans, and ISO 22301-aligned testing.",
    testid: "service-card-continuity",
  },
  {
    icon: FileCheck2,
    title: "Implementation & Training",
    blurb: "We do not stop at the binder. We implement, train your teams, and measure adoption.",
    testid: "service-card-impl",
  },
  {
    icon: ShieldAlert,
    title: "Armed Guards",
    blurb: "Licensed armed personnel — vetted, trained, and qualified to standards that exceed every state-mandated minimum we operate under.",
    testid: "service-card-armed",
  },
  {
    icon: Church,
    title: "Church Security",
    blurb: "Industrial-grade security planning and armed guards for houses of worship — rigorous, discreet, and priced for congregations, not corporations. Chesterton is a Christian company.",
    testid: "service-card-church",
  },
];

// Completed years of practice since May 2010. Ticks over on each May anniversary.
const yearsInPractice = (() => {
  const now = new Date();
  const startYear = 2010;
  const startMonth = 4; // May (0-indexed)
  return now.getMonth() >= startMonth
    ? now.getFullYear() - startYear
    : now.getFullYear() - startYear - 1;
})();

const stats = [
  { value: String(yearsInPractice), label: "Years In Practice" },
  { value: "MARITIME", label: "Aerospace · Space · Defense" },
  { value: "ITAR", label: "EAR · OFAC · CFATS" },
  { value: "0", label: "Audit Failures" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-screen flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/60 to-slate-950" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pb-24 pt-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-9">
              <p className="overline anim-up">Industrial Safety · Security · Export Compliance</p>
              <h1 className="anim-up anim-delay-1 font-serif text-5xl sm:text-7xl lg:text-8xl text-slate-50 leading-[0.95] tracking-tight mt-6">
                Fortifying the<br />
                <span className="italic text-amber-500">commerce</span> that<br />
                moves the world.
              </h1>
              <p className="anim-up anim-delay-2 mt-8 text-slate-300 text-lg max-w-xl leading-relaxed">
                Chesterton Security builds the plans, controls, and trade-compliance
                programs that let serious operators ship, manufacture, and grow
                without the headlines.
              </p>
              <div className="anim-up anim-delay-3 mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="group inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
                >
                  Explore Services
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="group inline-flex items-center gap-3 border border-slate-600 hover:border-slate-300 text-slate-100 px-7 py-4 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
                >
                  Talk to a Principal
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-slate-900 border-y border-slate-800 overflow-hidden py-6">
        <div className="flex whitespace-nowrap marquee-track text-slate-500 text-xs uppercase tracking-[0.3em]">
          {Array.from({ length: 2 }).map((_, idx) => (
            <div key={idx} className="flex items-center gap-12 px-6 shrink-0">
              <span>ITAR Counsel</span><span className="text-amber-500">◆</span>
              <span>EAR Classification</span><span className="text-amber-500">◆</span>
              <span>OSHA VPP Star</span><span className="text-amber-500">◆</span>
              <span>OFAC Screening</span><span className="text-amber-500">◆</span>
              <span>CFATS Tier I–IV</span><span className="text-amber-500">◆</span>
              <span>C-TPAT Validation</span><span className="text-amber-500">◆</span>
              <span>ISO 28000</span><span className="text-amber-500">◆</span>
              <span>Crisis Response</span><span className="text-amber-500">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* CREDIBILITY / WHY US */}
      <section data-testid="home-why" className="bg-slate-950 py-28 lg:py-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="overline mb-6">The Practice</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-slate-50 leading-[1.05] tracking-tight">
                Quiet work for<br />
                <span className="italic text-amber-500">consequential</span> clients.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-8 text-slate-300 text-lg leading-relaxed">
              <p>
                Founded by attorney Jack Shelton, Chesterton advises operators
                across maritime, aerospace, space, and defense on the programs
                that keep them moving — and out of the federal register.
              </p>
              <p className="text-slate-400">
                We write security plans you can defend in a deposition,
                facility-security programs your team will actually use, and
                export-compliance regimes that survive a BIS or DDTC audit.
                Then we sit beside your people and implement them.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-800 border border-slate-800 mt-12">
                {stats.map((s) => (
                  <div
                    key={s.label}
                    data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="bg-slate-950 p-6"
                  >
                    <div className="font-serif text-4xl text-amber-500">{s.value}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-2">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section data-testid="home-services" className="bg-slate-900 py-28 lg:py-40 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <p className="overline mb-6">Capabilities</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-slate-50 leading-[1.05] tracking-tight max-w-2xl">
                Eleven disciplines.<br />
                One integrated <span className="italic text-amber-500">defense</span>.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-section-cta"
              className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 text-xs uppercase tracking-[0.22em] font-medium link-underline"
            >
              All Capabilities
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
            {services.map((s, i) => {
              const Icon = s.icon;
              const isLastOdd = services.length % 2 === 1 && i === services.length - 1;
              return (
                <div
                  key={s.title}
                  data-testid={s.testid}
                  className={`group bg-slate-900 hover:bg-slate-950 transition-colors p-10 lg:p-12 relative ${
                    isLastOdd ? "md:col-span-2" : ""
                  }`}
                >
                  <div className="flex items-start justify-between mb-12">
                    <Icon className="w-8 h-8 text-amber-500" strokeWidth={1.2} />
                    <span className="font-serif text-slate-700 text-2xl">0{i + 1}</span>
                  </div>
                  <h3 className="font-serif text-3xl text-slate-50 mb-4">{s.title}</h3>
                  <p className="text-slate-400 leading-relaxed max-w-md">{s.blurb}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.22em] text-slate-300 group-hover:text-amber-500 transition-colors"
                  >
                    Read more
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EDITORIAL PULLQUOTE */}
      <section className="relative bg-slate-950 py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${SERVICES_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/40" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
          <p className="overline mb-8">From the Founder</p>
          <blockquote className="font-serif text-3xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.05] tracking-tight">
            “A compliance program is not a binder.
            It is a <span className="italic text-amber-500">posture</span> — a way the
            organization stands when no one is watching.”
          </blockquote>
          <p className="mt-10 text-slate-400 text-sm uppercase tracking-[0.22em]">
            — Jack Shelton, Founding Principal
          </p>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <h2 className="lg:col-span-8 font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.05] tracking-tight">
              When the audit comes,<br />
              <span className="italic text-amber-500">be unbothered.</span>
            </h2>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                data-testid="home-cta-button"
                className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-white px-8 py-5 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
              >
                Request a Consultation
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
